// Chat conversation export (Markdown / Plain text / Word). Ported from the original Studio Reachy
// chat (packages/web R3ChatPage.tsx), which was lost in the AWS-Reachy → CF-Ros move. Faithful port:
// same formats, same layout, same filename convention. Self-contained (no shared-package dependency).

import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx'

export type ExportFormat = 'md' | 'docx' | 'txt'

export interface ConversationMessage {
  role: 'user' | 'assistant'
  content: string
}

const INTENT_LABELS: Record<string, string> = {
  survey_guard: 'Survey Guard',
  general_conversation: 'General',
  discovery_interviewer: 'Discovery Interviewer',
  transcription: 'Transcription',
  crosstabs_builder: 'Crosstabs Builder',
  tab_plan: 'Tab Plan',
  general: 'General',
  greeting: 'Greeting',
  escalation: 'Escalation',
}

function intentLabel(intentSummary?: string): string {
  return intentSummary ? (INTENT_LABELS[intentSummary] ?? intentSummary) : 'General'
}

function formatExportDate(ts: number): string {
  const ms = ts < 1_000_000_000_000 ? ts * 1000 : ts
  return new Date(ms).toLocaleString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

function baseFilename(intentSummary?: string, sessionId?: string): string {
  const theme = intentSummary
    ? intentLabel(intentSummary).toLowerCase().replace(/\s+/g, '-')
    : 'chat'
  const suffix = sessionId ? `-${sessionId.slice(0, 8)}` : `-${Date.now()}`
  return `ros-${theme}${suffix}`
}

/** Strip markdown to plain text for the TXT + DOCX exports. */
function mdToPlainText(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, '')            // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')        // bold
    .replace(/\*(.+?)\*/g, '$1')            // italic
    .replace(/__(.+?)__/g, '$1')            // bold alt
    .replace(/_(.+?)_/g, '$1')              // italic alt
    .replace(/`{3}[\s\S]*?`{3}/g, '')       // fenced code blocks
    .replace(/`(.+?)`/g, '$1')              // inline code
    .replace(/^\s*[-*+]\s+/gm, '• ')        // unordered lists
    .replace(/^\s*\d+\.\s+/gm, '')          // ordered lists
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')     // links
    .replace(/!\[.*?\]\(.+?\)/g, '')        // images
    .replace(/^[-*_]{3,}\s*$/gm, '---')     // horizontal rules
    .replace(/\n{3,}/g, '\n\n')             // collapse excess blank lines
    .trim()
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function exportAsMd(messages: ConversationMessage[], intentSummary?: string, startedAt?: number, userName?: string, sessionId?: string) {
  const theme = intentLabel(intentSummary)
  const dateStr = startedAt ? formatExportDate(startedAt) : new Date().toLocaleString()
  const userLabel = userName || 'You'
  const lines: string[] = [`# Conversation - ${theme}`, `**Date and Time:** ${dateStr}`, '']
  for (const msg of messages) {
    lines.push(msg.role === 'user' ? `**${userLabel}:**` : '**Ros:**')
    lines.push(msg.content)
    lines.push('')
  }
  downloadBlob(new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' }), `${baseFilename(intentSummary, sessionId)}.md`)
}

function exportAsTxt(messages: ConversationMessage[], intentSummary?: string, startedAt?: number, userName?: string, sessionId?: string) {
  const theme = intentLabel(intentSummary)
  const dateStr = startedAt ? formatExportDate(startedAt) : new Date().toLocaleString()
  const userLabel = userName || 'You'
  const lines: string[] = [`Conversation - ${theme}`, `Date and Time: ${dateStr}`, '', '-'.repeat(48), '']
  for (const msg of messages) {
    lines.push(msg.role === 'user' ? `${userLabel}:` : 'Ros:')
    lines.push(mdToPlainText(msg.content))
    lines.push('')
  }
  downloadBlob(new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' }), `${baseFilename(intentSummary, sessionId)}.txt`)
}

async function exportAsDocx(messages: ConversationMessage[], intentSummary?: string, startedAt?: number, userName?: string, sessionId?: string) {
  const theme = intentLabel(intentSummary)
  const dateStr = startedAt ? formatExportDate(startedAt) : new Date().toLocaleString()
  const userLabel = userName || 'You'
  const docChildren: Paragraph[] = [
    new Paragraph({ text: `Conversation - ${theme}`, heading: HeadingLevel.HEADING_1 }),
    new Paragraph({ children: [new TextRun({ text: 'Date and Time: ', bold: true }), new TextRun({ text: dateStr })], spacing: { after: 200 } }),
  ]
  for (const msg of messages) {
    const label = msg.role === 'user' ? userLabel : 'Ros'
    const plainContent = mdToPlainText(msg.content)
    docChildren.push(new Paragraph({
      children: [new TextRun({ text: `${label}:`, bold: true })],
      spacing: { before: 200, after: 80 },
      alignment: msg.role === 'user' ? AlignmentType.RIGHT : AlignmentType.LEFT,
    }))
    // Split on double newlines to preserve paragraph breaks.
    for (const para of plainContent.split(/\n\n+/)) {
      const trimmed = para.trim()
      if (trimmed) {
        docChildren.push(new Paragraph({ text: trimmed, spacing: { after: 80 }, alignment: msg.role === 'user' ? AlignmentType.RIGHT : AlignmentType.LEFT }))
      }
    }
  }
  const doc = new Document({ sections: [{ children: docChildren }] })
  const buffer = await Packer.toBlob(doc)
  downloadBlob(buffer, `${baseFilename(intentSummary, sessionId)}.docx`)
}

/** Export a conversation to the chosen format and trigger a browser download. */
export async function exportConversation(
  format: ExportFormat,
  messages: ConversationMessage[],
  intentSummary?: string,
  startedAt?: number,
  userName?: string,
  sessionId?: string,
) {
  if (format === 'md') exportAsMd(messages, intentSummary, startedAt, userName, sessionId)
  else if (format === 'txt') exportAsTxt(messages, intentSummary, startedAt, userName, sessionId)
  else await exportAsDocx(messages, intentSummary, startedAt, userName, sessionId)
}
